/**
 * SOLID Review
 * S — Strong. sole job now: HTTP/WS link construction, the auth-header link, the split between them, the
 *     slow-request UI hint, and the ApolloClient instance itself — "the Apollo client" the file
 *     name promises. Token storage moved to `sessionTokenStorage.ts` and connection-health
 *     monitoring moved to `wsConnectionMonitor.ts` (Part 2/3 of this pass); both are re-exported
 *     below so no external call site changes. Strong (was Concern).
 * O — Good. The mobile-recovery logic now lives entirely in `wsConnectionMonitor.ts`, tuned via constants
 *     there; this file doesn't need to change for that axis at all.
 * L — N/A — no inheritance hierarchy; this is module-level setup code, not a class.
 * I — Acceptable. still re-exports the token/session/connection-health surface for backward compatibility, but
 *     each concern now has its own real module — a consumer that only needs `apolloClient` can
 *     import it from here; one that only needs storage helpers can import `sessionTokenStorage.ts`
 *     directly instead. Good (was Concern).
 * D — Good. Previously reached directly into `application/stores/uiStore`
 *     (useUIStore.getState().setConnectionBanner) from Infrastructure — a real, citable layering
 *     violation. Fixed via `setConnectionBannerHandlers`: this module now only calls a locally- held,
 *     injectable `connectionBannerHandlers` pair (no-ops until set); the real `useUIStore`-backed handlers
 *     are supplied once from `framework/App.tsx` (the composition root), matching
 *     `createSlowRequestLink`'s existing injected-callback port shape.
 */
import { ApolloClient, InMemoryCache, HttpLink, split } from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { setContext } from '@apollo/client/link/context';
import { createSlowRequestLink, IServiceSlowRequestLinkOptions } from './slowRequestLink';
import { PROD_GRAPHQL_HTTP_URL } from '../../shared/appConfig';
import { getSessionToken } from '../storage/sessionTokenStorage';
import { wsClient } from './wsConnectionMonitor';

// The session-token and resume-session re-exports that used to live here are GONE (2026-08-17).
// They were pure tabPersistentStorage helpers that only sat in this module for historical
// reasons, and re-exporting them meant six files imported an Apollo-laden module to get a
// storage function — which is what made four of them dependency-cruiser violations. The helpers
// now live in infrastructure/storage/sessionTokenStorage; import them from there.

export {
  type WsConnectionState,
  getWsConnectionState,
  onWsConnectionStateChange,
  forceReconnect,
  reidentify,
  wsClient,
} from './wsConnectionMonitor';

const env = (import.meta as unknown as { env: Record<string, string> }).env ?? {};
const isDev = env['DEV'] === 'true' || import.meta.env?.DEV === true;

const HTTP_URL = env['VITE_GRAPHQL_HTTP_URL'] ?? (
  isDev
    ? `${window.location.protocol}//${window.location.host}/graphql`
    : PROD_GRAPHQL_HTTP_URL
);

// ---------------------------------------------------------------------------
// HTTP link
// ---------------------------------------------------------------------------
const httpLink = new HttpLink({ uri: HTTP_URL });

// ---------------------------------------------------------------------------
// Auth link – injects Authorization header on every HTTP request
// ---------------------------------------------------------------------------
const authLink = setContext((_, previousContext: Record<string, unknown>) => {
  const token = getSessionToken();
  const existingHeaders =
    (previousContext['headers'] as Record<string, string> | undefined) ?? {};

  return {
    headers: {
      ...existingHeaders,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
});

const wsLink = new GraphQLWsLink(wsClient);

// ---------------------------------------------------------------------------
// Split: subscriptions → WebSocket, everything else → HTTP
// ---------------------------------------------------------------------------
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  authLink.concat(httpLink),
);

// ---------------------------------------------------------------------------
// Apollo Client instance
// ---------------------------------------------------------------------------
// Surface a transient hint when the server is struggling. Load testing showed the backend degrades
// GRACEFULLY under saturation (slow, not erroring: p95 604ms normally vs 2.5s past the knee), so
// without this a player just sees an unresponsive board and starts refreshing — the worst possible
// response for an already-loaded server. Per-button in-flight labels cover the normal case; this
// covers the abnormal one. Mutations only (§12.36).
//
// This is Infrastructure, so it must not reach into Application state directly. The real
// onSlowStart/onSlowEnd handlers are supplied later via setConnectionBannerHandlers (called once
// from framework/App.tsx, the composition root) — until then these are harmless no-ops.
export interface IConfigHandlerConnectionBanner {
  readonly onSlowStart: () => void;
  readonly onSlowEnd: () => void;
}

let connectionBannerHandlers: IConfigHandlerConnectionBanner = {
  onSlowStart: () => {},
  onSlowEnd: () => {},
};

export function setConnectionBannerHandlers(handlers: IConfigHandlerConnectionBanner): void {
  connectionBannerHandlers = handlers;
}

const slowRequestLinkOptions: IServiceSlowRequestLinkOptions = {
  thresholdMs: 1500,
  onSlowStart: () => connectionBannerHandlers.onSlowStart(),
  onSlowEnd: () => connectionBannerHandlers.onSlowEnd(),
};
const slowRequestLink = createSlowRequestLink(slowRequestLinkOptions);

export const apolloClient = new ApolloClient({
  link: slowRequestLink.concat(splitLink),
  // NO typePolicies, deliberately (2026-08-26). Six were declared —
  // RoomEntity / PlayerEntity / GameSessionEntity / TurnEntity / RoundEntity / LocalUserEntity,
  // each `keyFields: ['id']` — and NONE of those type names exists in the backend SDL, which calls
  // them Room, Player, Round and so on (checked against backend/src/framework/graphql/schema/).
  // A type policy only ever applies to a response object whose __typename matches, so all six were
  // inert from the day they were written: dead cache config from the same fabrication that produced
  // the operation registry deleted in ComponentInfrastructure.ts.
  //
  // Not "fixed" by renaming them to the real types, because that would also be a no-op:
  // InMemoryCache already normalises on `id` when a type has one, which Room/Player/Round all do.
  // Restore an explicit policy only for a type that genuinely needs non-default key fields.
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all' as any,
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all' as any,
    },
    mutate: {
      errorPolicy: 'all' as any,
    },
  },
});

export default apolloClient;
